import { BASELINE_SIDE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Dialog, DialogContent, ErrorState, Field, Select, Skeleton } from "@sagot/ui";
import { DIFF_DRAFT, DIFF_LIVE, type VersionItem } from "../p4-types";
import { useVersionDiff } from "../use-workflows-p4";
import { DiffBody } from "./diff-body";

const V = "engage.workflows.versions";

interface VersionCompareProps {
  versions: readonly VersionItem[];
  base: string;
  target: string;
  /** The editor holds changes that are not saved yet: the draft in the comparison is the saved one. */
  unsaved: boolean;
  workflowId: string;
  /** A default workflow can be compared with its baseline: the original it started from. */
  withBaseline?: boolean;
  /** Focus goes here when the dialog closes (a dialog opened from a menu item). */
  onCloseAutoFocus?: (event: Event) => void;
  onChange: (base: string, target: string) => void;
  onClose: () => void;
}

/** What a side is called: the draft, the live version, or "Version 3". */
export function sideLabel(value: string, versions: readonly VersionItem[], t: (key: string, options?: Record<string, unknown>) => string): string {
  if (value === DIFF_DRAFT) return t(`${V}.draft`);
  if (value === BASELINE_SIDE) return t("engage.workflows.defaults.compare.baseline");
  const number = value === DIFF_LIVE ? versions.find((version) => version.live)?.number : Number(value);
  const live = value === DIFF_LIVE || versions.find((version) => version.number === number)?.live === true;
  return number === undefined ? t(`${V}.live`) : t(live ? `${V}.versionLive` : `${V}.versionName`, { number });
}

/** The select lists versions by number: the `live` alias the panel opens a comparison with is shown as the live version. */
export function optionValue(value: string, versions: readonly VersionItem[]): string {
  if (value !== DIFF_LIVE) return value;
  const live = versions.find((version) => version.live);
  return live ? String(live.number) : value;
}

function Choices({ versions, withBaseline }: { versions: readonly VersionItem[]; withBaseline: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <option value={DIFF_DRAFT}>{t(`${V}.draft`)}</option>
      {withBaseline ? <option value={BASELINE_SIDE}>{t("engage.workflows.defaults.compare.baseline")}</option> : null}
      {versions.map((version) => <option key={version.id} value={String(version.number)}>{sideLabel(String(version.number), versions, t)}</option>)}
    </>
  );
}

/** Two versions (or the draft) side by side: steps added, removed, changed or moved are marked on each side. */
export function VersionCompare({ versions, base, target, unsaved, workflowId, withBaseline = false, onCloseAutoFocus, onChange, onClose }: VersionCompareProps) {
  const { t } = useTranslation();
  const diff = useVersionDiff(workflowId, base, target);
  const data = diff.data;
  const title = withBaseline ? t("engage.workflows.defaults.compare.title") : t(`${V}.compareTitle`);
  const hint = withBaseline ? t("engage.workflows.defaults.compare.hint") : t(`${V}.compareHint`);
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={title} description={hint} closeLabel={t("common.close")} className="max-w-5xl" onCloseAutoFocus={onCloseAutoFocus}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t(`${V}.compareBase`)}>
              <Select value={optionValue(base, versions)} onChange={(e) => { onChange(e.target.value, target); }}><Choices versions={versions} withBaseline={withBaseline} /></Select>
            </Field>
            <Field label={t(`${V}.compareTarget`)}>
              <Select value={optionValue(target, versions)} onChange={(e) => { onChange(base, e.target.value); }}><Choices versions={versions} withBaseline={withBaseline} /></Select>
            </Field>
          </div>
          {unsaved && (base === DIFF_DRAFT || target === DIFF_DRAFT) ? <p className="rounded-md bg-info-soft p-3 text-xs" role="note">{t(`${V}.unsavedNote`)}</p> : null}
          {diff.isError ? <ErrorState title={t(`${V}.compareFailed`)} onRetry={() => void diff.refetch()} retryLabel={t("common.retry")} /> : null}
          {diff.isPending ? <Skeleton className="h-48 rounded-md" aria-hidden="true" /> : null}
          {data ? <DiffBody data={data} baseTitle={sideLabel(base, versions, t)} targetTitle={sideLabel(target, versions, t)} /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
